import { useContext } from "react";
import { HostPortalContext } from "./context";
import { usePortalScope } from "../portalScope";

export interface PortalBranding {
  /** True when this surface should render a brand's identity, not Reveshare's. */
  isWhiteLabel: boolean;
  /** Display name — the brand's portal name, or "Reveshare". */
  portalName: string;
  /** Brand logo URL; null means "no logo" (white-label) or "use the default mark". */
  portalLogo: string | null;
  /**
   * False while either source is still resolving. Surfaces should render a
   * neutral placeholder rather than the Reveshare mark until this is true:
   * flashing "Reveshare" inside a brand's white-label portal is the leak
   * ticket 4 exists to close.
   */
  isReady: boolean;
  /**
   * The white-label brand's BrandDetail id, when this portal wears one.
   * Signup sends it as `homeBrandDetailId` so affiliates recruited through a
   * branded portal land scoped (ticket 5). Null when unbranded.
   */
  homeBrandDetailId: string | null;
  /**
   * The brand's accent colours, for the runtime theme (ticket 11). Null when
   * unbranded or when the portal's tier does not carry colours — the default
   * Reveshare scale stays in place. Applied by PortalBrandingTheme.
   */
  primaryColour: string | null;
  secondaryColour: string | null;
}

const DEFAULT_NAME = "Reveshare";

/**
 * The single source of truth for what identity the portal wears (ticket 4).
 *
 * Two sources, in priority order:
 * 1. The authenticated affiliate's portal scope (inside the affiliate shell) —
 *    a scoped affiliate sees their home brand's branding wherever they are.
 * 2. The page hostname (everywhere, including login/signup before auth) — a
 *    `<slug>.reveshare.com` portal wears its brand's identity.
 *
 * Anything else renders as Reveshare once both sources have settled.
 */
export function usePortalBranding(): PortalBranding {
  const host = useContext(HostPortalContext);
  const { scope, isLoading: scopeLoading, hasProvider } = usePortalScope();

  if (hasProvider && scope.isScoped) {
    return {
      isWhiteLabel: true,
      portalName: scope.portalName || scope.homeBrandName || DEFAULT_NAME,
      portalLogo: scope.portalLogo,
      isReady: true,
      homeBrandDetailId: scope.homeBrandId,
      primaryColour: scope.primaryColour,
      secondaryColour: scope.secondaryColour,
    };
  }

  if (host.branding) {
    return {
      isWhiteLabel: true,
      portalName: host.branding.portalName,
      portalLogo: host.branding.portalLogo,
      isReady: true,
      homeBrandDetailId: host.branding.brandDetailId,
      primaryColour: host.branding.primaryColour,
      secondaryColour: host.branding.secondaryColour,
    };
  }

  return {
    isWhiteLabel: false,
    portalName: DEFAULT_NAME,
    portalLogo: null,
    isReady: !host.isLoading && (!hasProvider || !scopeLoading),
    homeBrandDetailId: null,
    primaryColour: null,
    secondaryColour: null,
  };
}
